import type { ReactElement, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  CalendarCheck,
  CalendarDays,
  ClipboardList,
  FileText,
  History,
  Home as HomeIcon,
  RefreshCw,
  ShoppingCart,
  Snowflake,
  Star,
  Ticket,
} from "lucide-react";
import Bonuses from "@/pages/Bonuses";
import BuyTicket from "@/pages/BuyTicket";
import Certificates from "@/pages/Certificates";
import Classes from "@/pages/Classes";
import FileUploader from "@/pages/FileUploader";
import FreezeApplication from "@/pages/FreezeApplication";
import Home from "@/pages/Home";
import PaymentsSuccess from "@/pages/PaymentsSuccess";
import PaymentsFail from "@/pages/PaymentsFail";
import PurchaseHistory from "@/pages/PurchaseHistory";
import RefundApplication from "@/pages/RefundApplication";
import SignUp from "@/pages/SignUp";
import SwimmersDiary from "@/pages/SwimmersDiary";
import TestError from "@/pages/TestError";
import Tickets from "@/pages/Tickets";
import VisitsHistory from "@/pages/VisitsHistory";
import Auth from "@/pages/Auth";
import {
  BONUSES,
  BUY_TICKET,
  CERTIFICATES,
  CLASSES,
  FILEUPLOADER,
  FREEZE_APPLICATION,
  HOME_ROUTE,
  LOGIN_ROUTE,
  PAYMENTS_FAIL,
  PAYMENTS_SUCCESS,
  PURCHASE_HISTORY,
  REFUND_APPLICATION,
  REGISTRATION_ROUTE,
  SIGN_UP,
  SIGN_UP_GROUPS,
  SWIMMERS_DIARY,
  TEST_ERROR_ROUTE,
  TICKETS,
  VISITS_HISTORY,
} from "@/utils/const";

export interface MenuItem {
  to: string;
  icon: ReactNode;
  text: string;
  showForPhone?: string;
  keywords?: string[];
}

export interface MenuGroup {
  id: string;
  label: string;
  items: MenuItem[];
}

type MenuGroupId = "navigation" | "subscriptions" | "history" | "applications";

const MENU_GROUP_ORDER: MenuGroupId[] = ["navigation", "subscriptions", "history", "applications"];

const MENU_GROUP_LABELS: Record<MenuGroupId, string> = {
  navigation: "Навигация",
  subscriptions: "Абонементы",
  history: "История",
  applications: "Заявления",
};

export type MenuConfig = Omit<MenuItem, "to" | "icon"> & {
  groupId: MenuGroupId;
  icon: LucideIcon;
};

export interface AppRoute {
  path: string;
  element: ReactElement;
}

export interface DashboardRoute extends AppRoute {
  menu?: MenuConfig;
}

const iconClassName = "h-[18px] w-[18px]";

const renderIcon = (Icon: LucideIcon) => <Icon className={iconClassName} />;

export const dashboardRoutes: DashboardRoute[] = [
  {
    path: HOME_ROUTE,
    element: <Home />,
    menu: {
      groupId: "navigation",
      icon: HomeIcon,
      text: "Главная",
      keywords: ["home", "главная", "main"],
    },
  },
  {
    path: CLASSES,
    element: <Classes />,
    menu: {
      groupId: "navigation",
      icon: ClipboardList,
      text: "Мои занятия",
      keywords: ["classes", "занятия", "lessons", "уроки"],
    },
  },
  {
    path: SIGN_UP,
    element: <SignUp />,
    menu: {
      groupId: "navigation",
      icon: CalendarDays,
      text: "Расписание",
      keywords: ["schedule", "расписание", "calendar", "запись"],
    },
  },
  {
    path: SIGN_UP_GROUPS,
    element: <SignUp />,
  },
  {
    path: TICKETS,
    element: <Tickets />,
    menu: {
      groupId: "subscriptions",
      icon: Ticket,
      text: "Абонементы",
      keywords: ["tickets", "абонементы", "подписка"],
    },
  },
  {
    path: BUY_TICKET,
    element: <BuyTicket />,
    menu: {
      groupId: "subscriptions",
      icon: ShoppingCart,
      text: "Купить абонемент",
      keywords: ["buy", "купить", "оплата", "purchase"],
    },
  },
  {
    path: PURCHASE_HISTORY,
    element: <PurchaseHistory />,
    menu: {
      groupId: "subscriptions",
      icon: History,
      text: "Последние оплаты",
      keywords: ["payments", "оплаты", "history", "история"],
    },
  },
  {
    path: BONUSES,
    element: <Bonuses />,
    menu: {
      groupId: "subscriptions",
      icon: Star,
      text: "Бонусный счёт",
      keywords: ["bonuses", "бонусы", "баллы", "points"],
    },
  },
  {
    path: VISITS_HISTORY,
    element: <VisitsHistory />,
    menu: {
      groupId: "history",
      icon: CalendarCheck,
      text: "История посещений",
      keywords: ["visits", "посещения", "attendance"],
    },
  },
  {
    path: SWIMMERS_DIARY,
    element: <SwimmersDiary />,
    menu: {
      groupId: "history",
      icon: BookOpen,
      text: "Дневник пловца",
      keywords: ["diary", "дневник", "achievements", "достижения"],
    },
  },
  {
    path: CERTIFICATES,
    element: <Certificates />,
    menu: {
      groupId: "history",
      icon: FileText,
      text: "Справки",
      keywords: ["certificates", "справки", "documents", "документы"],
    },
  },
  {
    path: REFUND_APPLICATION,
    element: <RefundApplication />,
    menu: {
      groupId: "applications",
      icon: RefreshCw,
      text: "Заявление на возврат",
      keywords: ["refund", "возврат", "заявление"],
    },
  },
  {
    path: FREEZE_APPLICATION,
    element: <FreezeApplication />,
    menu: {
      groupId: "applications",
      icon: Snowflake,
      text: "Заморозить абонемент",
      keywords: ["freeze", "заморозка", "пауза"],
    },
  },
  {
    path: FILEUPLOADER,
    element: <FileUploader />,
  },
];

export const authRoutes: AppRoute[] = [
  { path: LOGIN_ROUTE, element: <Auth /> },
  { path: REGISTRATION_ROUTE, element: <Auth /> },
];

export const publicRoutes: AppRoute[] = [
  { path: TEST_ERROR_ROUTE, element: <TestError /> },
  { path: PAYMENTS_SUCCESS, element: <PaymentsSuccess /> },
  { path: PAYMENTS_FAIL, element: <PaymentsFail /> },
];

export const menuGroups: MenuGroup[] = (() => {
  const byId = new Map<MenuGroupId, MenuGroup>();
  for (const route of dashboardRoutes) {
    if (!route.menu) continue;
    const { groupId, icon, text, showForPhone, keywords } = route.menu;
    let group = byId.get(groupId);
    if (!group) {
      group = { id: groupId, label: MENU_GROUP_LABELS[groupId], items: [] };
      byId.set(groupId, group);
    }
    group.items.push({ to: route.path, icon: renderIcon(icon), text, showForPhone, keywords });
  }
  return MENU_GROUP_ORDER.map((id) => byId.get(id)).filter((g): g is MenuGroup => Boolean(g));
})();

export const getAllMenuItems = (): MenuItem[] => menuGroups.flatMap((group) => group.items);
